/**
 * Maps raw MCP tool names to human-readable descriptions.
 * Multiple variants per tool for variety.
 */

const TOOL_DESCRIPTIONS = {
  // Search & Discovery
  company_name_search: [
    "Searching for companies...",
    "Looking up company names...",
    "Finding matching companies...",
  ],
  persons_name_search: [
    "Searching for people...",
    "Looking up person names...",
    "Finding matching contacts...",
  ],
  search_web: [
    "Searching the web...",
    "Researching online...",
    "Looking up information online...",
  ],

  // Async Search (trigger + poll)
  characteristics_search_trigger: [
    "Starting characteristics search...",
    "Launching characteristics search...",
    "Initiating company characteristics search...",
  ],
  characteristics_search_poll: [
    "Searching by characteristics...",
    "Scanning companies by characteristics...",
    "Matching company characteristics...",
  ],
  comparables_search_trigger: [
    "Finding comparable companies...",
    "Starting comparables search...",
    "Searching for similar companies...",
  ],
  comparables_search_poll: [
    "Checking comparables results...",
    "Waiting for comparables results...",
    "Retrieving comparable companies...",
  ],

  // Industry & Reference
  industry_codes_search: [
    "Searching by industry codes...",
    "Looking up industry classifications...",
    "Filtering by industry...",
  ],
  nomenclature_details: [
    "Loading reference data...",
    "Fetching nomenclature details...",
    "Retrieving classification data...",
  ],

  // Company Information
  company_basic_info: [
    "Retrieving company details...",
    "Loading company information...",
    "Fetching company profile...",
  ],
  company_financial_overview: [
    "Loading financial overview...",
    "Retrieving financial summary...",
    "Fetching financial data...",
  ],
  company_financial_statements: [
    "Loading financial statements...",
    "Retrieving detailed financials...",
    "Fetching financial reports...",
  ],
  company_shareholders_contacts: [
    "Looking up shareholders & contacts...",
    "Retrieving ownership information...",
    "Loading shareholder details...",
  ],

  // Person Information
  profiler_person_details: [
    "Retrieving person details...",
    "Loading contact information...",
    "Fetching person profile...",
  ],

  // Projects & Watchlist
  list_projects: [
    "Loading your projects...",
    "Retrieving project list...",
    "Fetching saved projects...",
  ],
  get_project_results: [
    "Loading project results...",
    "Retrieving project companies...",
    "Fetching project data...",
  ],
  save_search_project: [
    "Saving search as project...",
    "Creating project from search...",
    "Saving project...",
  ],
  get_watchlist: [
    "Loading your watchlist...",
    "Retrieving saved companies...",
    "Fetching watchlist...",
  ],
  add_remove_watchlist: [
    "Updating your watchlist...",
    "Modifying watchlist...",
    "Saving watchlist changes...",
  ],

  // Exclusions
  exclude_company_from_search: [
    "Excluding company from results...",
    "Removing company from search...",
    "Filtering out company...",
  ],
  exclude_company_from_project: [
    "Excluding company from project...",
    "Removing company from project...",
    "Updating project list...",
  ],
};

/**
 * Strip MCP gateway prefix from tool name.
 * Gateway names: "company-name-search-dev__company_name_search" -> "company_name_search"
 */
export function stripGatewayPrefix(rawToolName) {
  const idx = rawToolName.indexOf('__');
  return idx === -1 ? rawToolName : rawToolName.slice(idx + 2);
}

/**
 * Generate a random index using crypto.getRandomValues (not Math.random).
 * Used for non-security-critical UI variety, but avoids SonarQube PRNG hotspot.
 */
function randomIndex(length) {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return array[0] % length;
}

/**
 * Get a random human-readable description for a tool.
 */
export function getToolDescription(rawToolName) {
  const baseName = stripGatewayPrefix(rawToolName || '');
  if (!baseName) return 'Working...';
  const descriptions = TOOL_DESCRIPTIONS[baseName];
  if (!descriptions || descriptions.length === 0) {
    // Unknown tool - format the base name as fallback
    return `Running ${baseName.replaceAll('_', ' ')}...`;
  }
  return descriptions[randomIndex(descriptions.length)];
}
